import * as vp from 'vopay';

import { FunctionComponent, useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import AccountContext from '../AccountContext';
import { usePartnerLogin } from '@/providers/PartnerLogin.provider';
import usePostSendOnboardingEmail from '@/hooks/useApi/usePostSendOnboardingEmail';
import SendOnboardingEmailContext, { SendOnboardingEmailCtx } from './AccountPanelContext';
import SendOnboardingEmailDialogue from './AccountPanelDialogue';
import { SendOnboardingEmailDialogueHandler, DialogueHandlerProps } from './types';
import styles from './AccountOnboardingPanel.module.scss';

const { Box, Button } = vp;

type AccountOnboardingPanelProps = {
  hasValidOnboardingUrl: boolean | null;
  onCopyClick: () => void;
  onActionClick: () => void;
  onBoardingLink: string | null;
};

type FC = FunctionComponent<AccountOnboardingPanelProps>;

const AccountOnboardingPanel: FC = ({
  hasValidOnboardingUrl,
  onActionClick,
  onCopyClick,
  onBoardingLink,
}: AccountOnboardingPanelProps) => {
  const {
    response: { isInsideSales, isRoleViewOnly },
  } = usePartnerLogin();
  const [copiedLabel, setCopiedLabel] = useState('Copy URL');

  const { details } = useContext(AccountContext);
  const { accountId } = useParams<{ accountId: string }>();
  const [showSendOnboardingEmailConfirmAlert, setShowSendOnboardingEmailConfirmAlert] = useState(false);
  const [showSendOnboardingEmailSuccessAlert, setShowSendOnboardingEmailSuccessAlert] = useState(false);
  const [showSendOnboardingEmailErrorAlert, setShowSendOnboardingEmailErrorAlert] = useState(false);
  const [shouldShowAlert, setShouldShowAlert] = useState(false);
  const [sendOnboardingEmailError, setSendOnboardingEmailError] = useState('');
  const [onboardingEmail, postSendOnboardingEmail] = usePostSendOnboardingEmail({});

  const SendOnboardingEmail: SendOnboardingEmailCtx = {
    showSendOnboardingEmailConfirmAlert,
    showSendOnboardingEmailSuccessAlert,
    showSendOnboardingEmailErrorAlert,
    sendOnboardingEmailError,
  };

  const handleCopied = () => {
    setCopiedLabel('Copied!');
    onCopyClick();
  };

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<SendOnboardingEmailDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: SendOnboardingEmailDialogueHandler = {
        confirm: {
          success: {
            default: () => {
              setShowSendOnboardingEmailConfirmAlert(false);
              postSendOnboardingEmail({
                Name: details[accountId].accountName,
                Email: details[accountId].contact.email,
                Link: onBoardingLink || '',
                VopayAccountID: accountId,
              });
              setTimeout(() => {
                setShouldShowAlert(true);
              }, 1500);
            },
          },
          fail: {
            default: () => {
              setShowSendOnboardingEmailConfirmAlert(false);
            },
          },
        },
        send: {
          success: {
            default: () => {
              setShowSendOnboardingEmailSuccessAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowSendOnboardingEmailErrorAlert(false);
              setSendOnboardingEmailError(onboardingEmail.ErrorMessage);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  useEffect(() => {
    if (shouldShowAlert) {
      setShouldShowAlert(false);
      if (onboardingEmail.Success) {
        setShowSendOnboardingEmailSuccessAlert(true);
      } else {
        setShowSendOnboardingEmailErrorAlert(true);
      }
    }
  }, [onboardingEmail]);

  const buttons = [
    <Button
      id="account-onboarding"
      label={copiedLabel}
      isDisabled={!hasValidOnboardingUrl || isRoleViewOnly}
      height="28px"
      onClick={handleCopied}
    />,
    <Button
      id="account-onboarding"
      label={getButtonLabel()}
      isSubmitting={hasValidOnboardingUrl === null}
      height="28px"
      isDisabled={isRoleViewOnly}
      onClick={onActionClick}
    />,
    ...(isInsideSales
      ? [
          <Button
            id="send-onboarding-email"
            label="Send Application Form"
            isSubmitting={hasValidOnboardingUrl === null}
            isDisabled={hasValidOnboardingUrl === null || hasValidOnboardingUrl === false || isRoleViewOnly}
            height="28px"
            onClick={() => setShowSendOnboardingEmailConfirmAlert(true)}
          />,
        ]
      : []),
  ];

  const gridTemplateColumns = `repeat(${buttons.length}, 1fr)`;

  return (
    <Box flx="col" gap="sm" boundary="shadow" mb="lg" pt="md" pb="md" ps="md" pe="md" jc="sb" ai="fs">
      <h3>Activate this account</h3>
      <div className={styles.AccountOnboardingPanelContainer} style={{ gridTemplateColumns }}>
        <SendOnboardingEmailContext.Provider value={SendOnboardingEmail}>
          {buttons}
          <SendOnboardingEmailDialogue handleDialogue={handlers.handleDialogue} />
        </SendOnboardingEmailContext.Provider>
      </div>
    </Box>
  );

  function getButtonLabel() {
    if (!hasValidOnboardingUrl) return 'Open Application Form';
    return hasValidOnboardingUrl === null ? '' : 'Get Onboarding URL';
  }
};

export default AccountOnboardingPanel;
